import React from 'react';
import { NavLink } from 'react-router-dom';
import { PageContainer } from './PageContainer';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  LayoutDashboard,
  Gamepad2,
  Trophy,
  Users,
  Target,
  Bell,
  Shield,
  Flag,
  HelpCircle
} from 'lucide-react';

function AdminNavTab({ to, children, icon: Icon }) {
  return (
    <NavLink
      to={to}
      end={to === '/admin/dashboard'}
      className={({ isActive }) =>
        [
          'flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-sm font-semibold transition-colors',
          isActive
            ? 'bg-white text-zinc-900 shadow-md'
            : 'bg-white/10 text-zinc-200 hover:bg-white/15 hover:text-white'
        ].join(' ')
      }
    >
      <Icon className="h-4 w-4" strokeWidth={2} />
      {children}
    </NavLink>
  );
}

/**
 * Shared shell for /admin/* pages: matches app page-shell + dark admin header and section tabs.
 */
export function AdminLayout({ title, subtitle, actions, children, loading }) {
  const { t, language, isRTL } = useLanguage();
  const betsLabel = language === 'en' ? 'Bets' : 'הימורים';
  const notifLabel = language === 'en' ? 'Notifications' : 'התראות';

  return (
    <PageContainer dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="animate-fade-in space-y-8">
        <section className="overflow-hidden rounded-3xl bg-gradient-to-br from-zinc-900 via-zinc-950 to-brand-950 px-5 py-6 text-white shadow-xl ring-1 ring-zinc-800/80 sm:px-8 sm:py-7">
          <div className="flex flex-col gap-4 border-b border-white/10 pb-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 flex-1 flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/10 ring-1 ring-white/10">
                <Shield className="h-5 w-5 text-brand-300" strokeWidth={2} />
              </span>
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-brand-200/95">
                  {language === 'en' ? 'Administration' : 'ניהול'}
                </p>
                <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>
                {subtitle ? (
                  <p className="mt-1 text-sm text-zinc-300">{subtitle}</p>
                ) : null}
              </div>
            </div>
            {actions ? (
              <div className="flex flex-wrap items-center gap-2 sm:justify-end">{actions}</div>
            ) : null}
          </div>
          <nav
            className="mt-4 flex gap-2 overflow-x-auto pb-1 pt-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            aria-label={language === 'en' ? 'Admin sections' : 'מדורי ניהול'}
          >
            <AdminNavTab to="/admin/dashboard" icon={LayoutDashboard}>
              {t('adminDashboard')}
            </AdminNavTab>
            <AdminNavTab to="/admin/competitions" icon={Flag}>
              {t('manageCompetitions')}
            </AdminNavTab>
            <AdminNavTab to="/admin/games" icon={Gamepad2}>
              {t('manageGames')}
            </AdminNavTab>
            <AdminNavTab to="/admin/teams" icon={Trophy}>
              {t('manageTeams')}
            </AdminNavTab>
            <AdminNavTab to="/admin/questions" icon={HelpCircle}>
              {language === 'en' ? 'Predictions' : 'תחזיות'}
            </AdminNavTab>
            <AdminNavTab to="/admin/users" icon={Users}>
              {t('manageUsers')}
            </AdminNavTab>
            <AdminNavTab to="/admin/bets" icon={Target}>
              {betsLabel}
            </AdminNavTab>
            <AdminNavTab to="/admin/notifications" icon={Bell}>
              {notifLabel}
            </AdminNavTab>
          </nav>
        </section>

        {loading ? (
          <div className="flex min-h-[40vh] items-center justify-center">
            <div
              className="h-10 w-10 animate-spin rounded-full border-2 border-brand-500 border-t-transparent"
              aria-hidden
            />
          </div>
        ) : (
          children
        )}
      </div>
    </PageContainer>
  );
}
